import React, { useState, useEffect } from 'react';
import Button from '../common/Button';
import Input from '../common/Input';
import { getClinicServiceById, updateClinicService } from '../../services/api';

const EditClinicServiceModal = ({ isOpen, onClose, onSuccess, clinicServiceId }) => {
    const [formData, setFormData] = useState({
        basePrice: '',
        durationMinutes: '',
        description: '',
        isEnabled: true,
        hasPackages: false
    });
    const [originalData, setOriginalData] = useState(null);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(false);

    useEffect(() => {
        if (isOpen && clinicServiceId) {
            fetchClinicService();
        }
    }, [isOpen, clinicServiceId]);

    const fetchClinicService = async () => {
        setFetching(true);
        try {
            const response = await getClinicServiceById(clinicServiceId);
            if (response.success) {
                const data = response.data;
                setOriginalData(data);
                setFormData({
                    basePrice: data.basePrice?.toString() || '',
                    durationMinutes: data.durationMinutes?.toString() || '',
                    description: data.description || '',
                    isEnabled: data.isEnabled ?? true,
                    hasPackages: data.hasPackages ?? false
                });
            } else {
                setErrors({ submit: response.message || 'Failed to fetch clinic service' });
            }
        } catch (error) {
            setErrors({ submit: 'An unexpected error occurred' });
        } finally {
            setFetching(false);
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.basePrice || parseFloat(formData.basePrice) <= 0) {
            newErrors.basePrice = 'Valid price is required';
        }

        if (!formData.durationMinutes || parseInt(formData.durationMinutes) <= 0) {
            newErrors.durationMinutes = 'Valid duration is required';
        }

        if (formData.description && formData.description.trim().length < 5) {
            newErrors.description = 'Description must be at least 5 characters';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));

        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const getChangedFields = () => {
        if (!originalData) return {};

        const changes = {};

        // Check each field for changes
        if (parseFloat(formData.basePrice) !== originalData.basePrice) {
            changes.basePrice = parseFloat(formData.basePrice);
        }

        if (parseInt(formData.durationMinutes) !== originalData.durationMinutes) {
            changes.durationMinutes = parseInt(formData.durationMinutes);
        }

        if (formData.description !== originalData.description) {
            changes.description = formData.description.trim() || null;
        }

        if (formData.isEnabled !== originalData.isEnabled) {
            changes.isEnabled = formData.isEnabled;
        }

        if (formData.hasPackages !== originalData.hasPackages) {
            changes.hasPackages = formData.hasPackages;
        }

        return changes;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        const changedFields = getChangedFields();
        
        // If no changes, just close the modal
        if (Object.keys(changedFields).length === 0) {
            handleClose();
            return;
        }

        setLoading(true);
        try {
            const response = await updateClinicService(clinicServiceId, changedFields);
            
            if (response.success) {
                onSuccess(response.data);
                handleClose();
            } else {
                setErrors({ submit: response.message || 'Failed to update clinic service' });
            }
        } catch (error) {
            setErrors({ submit: 'An unexpected error occurred' });
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setFormData({
            basePrice: '',
            durationMinutes: '',
            description: '',
            isEnabled: true,
            hasPackages: false
        });
        setOriginalData(null);
        setErrors({});
        setLoading(false);
        setFetching(false);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                        <h2 className="text-xl font-semibold text-gray-900">Edit Clinic Service</h2>
                        <button
                            onClick={handleClose}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Loading State */}
                {fetching && (
                    <div className="px-6 py-8 text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                        <p className="text-gray-600">Loading service details...</p>
                    </div>
                )}

                {/* Form */}
                {!fetching && (
                    <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
                        {/* Price */}
                        <div>
                            <label htmlFor="basePrice" className="block text-sm font-medium text-gray-700 mb-1">
                                Price (₹) *
                            </label>
                            <Input
                                type="number"
                                id="basePrice"
                                name="basePrice"
                                value={formData.basePrice}
                                onChange={handleInputChange}
                                placeholder="0.00"
                                min="0"
                                step="0.01"
                                error={errors.basePrice}
                                required
                            />
                        </div>

                        {/* Duration */}
                        <div>
                            <label htmlFor="durationMinutes" className="block text-sm font-medium text-gray-700 mb-1">
                                Duration (minutes) *
                            </label>
                            <Input
                                type="number"
                                id="durationMinutes"
                                name="durationMinutes"
                                value={formData.durationMinutes}
                                onChange={handleInputChange}
                                placeholder="60"
                                min="1"
                                error={errors.durationMinutes}
                                required
                            />
                        </div>

                        {/* Description */}
                        <div>
                            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                                Clinic-Specific Description
                            </label>
                            <textarea
                                id="description"
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                                placeholder="Any specific details about how this clinic provides this service..."
                                rows={3}
                                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                    errors.description ? 'border-red-500' : 'border-gray-300'
                                }`}
                            />
                            {errors.description && (
                                <p className="text-sm text-red-600 mt-1">{errors.description}</p>
                            )}
                            <p className="text-xs text-gray-500 mt-1">
                                Optional: Add clinic-specific details about this service
                            </p>
                        </div>

                        {/* Active Status */}
                        <div className="flex items-center">
                            <input
                                type="checkbox"
                                id="isEnabled"
                                name="isEnabled"
                                checked={formData.isEnabled}
                                onChange={handleInputChange}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <label htmlFor="isEnabled" className="ml-2 block text-sm text-gray-900">
                                Service is active and available for booking
                            </label>
                        </div>

                        {/* Package Discounts */}
                        <div className="flex items-center">
                            <input
                                type="checkbox"
                                id="hasPackages"
                                name="hasPackages"
                                checked={formData.hasPackages}
                                onChange={handleInputChange}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <label htmlFor="hasPackages" className="ml-2 block text-sm text-gray-900">
                                This service offers package discounts
                            </label>
                        </div>
                        <p className="text-xs text-gray-500">
                            Check if this service has special pricing for multiple sessions or packages
                        </p>

                        {/* Submit Error */}
                        {errors.submit && (
                            <div className="bg-red-50 border border-red-200 rounded-md p-3">
                                <p className="text-sm text-red-600">{errors.submit}</p>
                            </div>
                        )}

                        {/* Action Buttons */}
                        <div className="flex space-x-3 pt-4">
                            <Button
                                type="button"
                                variant="secondary"
                                onClick={handleClose}
                                className="flex-1"
                                disabled={loading}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="primary"
                                className="flex-1"
                                loading={loading}
                            >
                                Update Service
                            </Button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default EditClinicServiceModal; 